import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  styled,
  Typography,
  useTheme
} from "@mui/material"
import React, { useEffect, useRef, useState } from "react"
import CloseIcon from "@mui/icons-material/Close"
import AppButton from "components/StyledComponents/AppButton"
import ImageCropper from "components/ImageCropper"
import { useDispatch, useSelector } from "react-redux"
import { Col, Row } from "react-bootstrap"
import AppTextField from "components/StyledComponents/AppTextField"
import StyledLabel from "components/StyledComponents/StyledLabel"
import AppDatePicker from "components/AppDatePicker"
import UploadPdfField from "../dynamic-forms/components/UploadPdfField"
import ImageUploadPreview from "components/FileUploadWithPreview/ImageUploadPreview"
import { capitalize } from "helpers/functions"
import ImageUploadField from "components/FileUpload/ImageUploadField"
import { uploadImageAction } from "redux/hotel/action"
import { hotelAction } from "redux/hotel/slice/user"
import { Pause, PlayArrow } from "@mui/icons-material"
import AppTextArea from "components/StyledComponents/AppTextArea"
import { generateSoundbitesAction } from "redux/marketing/actions"
import DidYouKnowFormHotel from "../dynamic-forms/components/FieldsModal/DidYouKnow"
import HistoryFormHotel from "../dynamic-forms/components/FieldsModal/History"
import { marketingActions } from "redux/marketing/slice/user"

const buttonText = {
  CANCEL: "Cancel",
  SAVE: "Save",
  UPDATE: "Update"
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    backgroundColor: "transparent",
    maxWidth: 1000,
    width: "100%"
  },
  "& .MuiDialogContent-root": {
    backgroundColor: theme.palette.background.default,
    borderRadius: 12,
    border: "none",
    width: "100%",
    padding: 35,
    boxSizing: "border-box"
  }
}))

const DynamicItemForm = ({
  open = false,
  onClose = () => {},
  isEdit = false,
  formik,
  dynamicFieldsGroup,
  title
}) => {
  const theme = useTheme()
  const dispatch = useDispatch()

  const [uploadType, setUploadType] = useState("")
  const [openCrop, setCrop] = useState(false)
  const [fileDetails, setFileDetails] = useState(null)
  const [imageObjectURL, setImageObjectUrl] = useState(null)
  const [selectedFieldPath, setSelectedFieldPath] = useState(null)
  const [imageKey, setImageKey] = useState("")
  const [sectionKey, setSectionKey] = useState("")
  const [isPlaying, setIsPlaying] = useState(false)
  const audioRef = useRef(null)
  const { soundbites } = useSelector((state) => state.marketing)

  const styles = {
    closeBtn: {
      color: theme.palette.text.primary,
      position: "absolute",
      top: "10px",
      right: "10px",
      backgroundColor: theme.palette.background.main
    }
  }

  const handleImageDelete = (formik, fieldPath, index, type, key, sectionKey) => {
    // Update Redux state
    dispatch(hotelAction.removeImageId({ key, sectionKey }))

    // Get current images from Redux
    const currentImages = formik.values?.[fieldPath] || []

    if (type === "multiple_image") {
      const updatedImages = currentImages.filter((_, i) => i !== index)
      formik.setFieldValue(fieldPath, updatedImages)
    } else {
      formik.setFieldValue(fieldPath, null)
    }
  }

  const onImageUpload = (image, fieldPath, key, type, sectionKey) => {
    if (type !== "image") {
      const existingImages = formik.values?.[fieldPath] || []
      if (existingImages.length >= 4) {
        alert("You can upload up to 4 images only")
        return
      }
    }
    setUploadType("image")
    setImageKey(key)
    setSelectedFieldPath(fieldPath)
    setSectionKey(sectionKey)
    setCrop(true)
    setFileDetails(image)
    setImageObjectUrl(URL.createObjectURL(image))
  }

  function handleCroppedImage(image, formik) {
    if (uploadType === "image") {
      formik.setFieldValue(selectedFieldPath, image)
      let data = new FormData()
      data.append("media", image)
      data.append("media_type", "IMAGE")
      data.append("content_type", image.type)
      dispatch(uploadImageAction({ data, fieldKey: imageKey, sectionKey }))
      setUploadType("")
    }
  }

  function showCropperModal(imageObjectURL, fileDetails, formik) {
    return (
      <ImageCropper
        setSelectedImage={(image) => handleCroppedImage(image, formik)}
        srcImage={imageObjectURL}
        fileDetail={fileDetails}
        aspectRatio={uploadType === "image" && 1}
        onClose={setCrop}
        openCrop={openCrop}></ImageCropper>
    )
  }
  const handleSoundbite = (soundbite) => {
    const payload = {
      text: soundbite,
      voice_id: formik.values?.soundbite_voice_id
    }
    dispatch(generateSoundbitesAction(payload))
    formik.setFieldTouched(`soundbite`, false)
  }

  const toggleSoundbite = () => {
    const soundbite_url = soundbites?.url || formik.values?.soundbite_url

    if (!soundbite_url) return

    if (isPlaying && audioRef.current) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
      }

      audioRef.current = new Audio(soundbite_url)
      audioRef.current.play()
      setIsPlaying(true)

      audioRef.current.onended = () => {
        setIsPlaying(false)
      }
    }
  }

  const getComponentType = (label) => {
    switch (label) {
      case "text":
        return "textField"
      case "image":
      case "images":
        return "imageUpload"
      case "file":
        return "uploadPdf"
      case "multiple_image":
        return "restaurantImageUpload"
      case "time":
        return "timeField"
      case "number_field":
        return "numberField"
      default:
        return "textField"
    }
  }

  useEffect(() => {
    if (!open) {
      formik.resetForm()
      dispatch(hotelAction.setImageId([]))
      dispatch(hotelAction.resetImageIds())
      dispatch(marketingActions.resetDidYouKnowImage())
      dispatch(marketingActions.resetHistoryMedia())
      dispatch(marketingActions.setSoundbites({}))
    }
  }, [open])

  return (
    <StyledDialog open={open}>
      <DialogContent>
        {showCropperModal(imageObjectURL, fileDetails, formik)}
        <Box className="d-flex justify-content-end">
          <IconButton onClick={() => onClose()} sx={styles.closeBtn}>
            <CloseIcon sx={{ height: "14px", width: "14px" }} />
          </IconButton>
        </Box>
        <Box className="w-100 d-flex justify-content-center mb-2">
          <Typography variant="h2">{isEdit ? `Edit ${title}` : "Add New"}</Typography>
        </Box>
        <Box flexDirection="column" className="mt-4">
          {dynamicFieldsGroup?.map((section, secIndex) => (
            <Box key={secIndex} className="mb-4">
              {!!section.fields?.length && (
                <Grid container alignItems="center" className="mb-3">
                  <Typography variant="h2" style={{ marginRight: "16px", whiteSpace: "nowrap" }}>
                    {section.label}
                  </Typography>
                  <div
                    style={{
                      flexGrow: 1,
                      height: "1px",
                      backgroundColor: "#E0E0E0"
                    }}
                  />
                </Grid>
              )}

              {section.fields?.map((field, fieldIndex) => {
                const componentType = getComponentType(field.type)
                const fieldPath = `${section.key}.${field.key}`
                switch (componentType) {
                  case "textField":
                  case "numberField":
                    return (
                      <Row className="w-100 m-0 mt-4" key={fieldIndex}>
                        <Col lg="12">
                          <AppTextField
                            id={fieldPath}
                            name={fieldPath}
                            type={
                              componentType === "numberField" || field?.key === "price"
                                ? "number"
                                : "text"
                            }
                            fullWidth
                            label={capitalize(field.label)}
                            isRequired={
                              section?.key === "common" &&
                              (field?.key === "name" || field?.key === "description")
                            }
                            isUpperCase={!false}
                            placeholder={`Enter ${field.label}`}
                            value={formik.values?.[section.key]?.[field.key]}
                            onChange={formik.handleChange(fieldPath)}
                            onBlur={formik.handleBlur(fieldPath)}
                            error={
                              formik.touched?.[section.key]?.[field.key] &&
                              Boolean(formik.errors?.[section.key]?.[field.key])
                            }
                            touched={formik.touched?.[section.key]?.[field.key]}
                            helperText={
                              formik.touched?.[section.key]?.[field.key] &&
                              formik.errors?.[section.key]?.[field.key]
                            }
                          />
                        </Col>
                      </Row>
                    )
                  case "datePicker":
                    return (
                      <Row className="w-100 m-0 mt-4" key={fieldIndex}>
                        <Col lg="12">
                          <Grid item sm={12} lg={6}>
                            <StyledLabel variant="body1Regular">{field.label}</StyledLabel>
                            <Box className="mt-2">
                              <AppDatePicker
                                inputFormat="dd MMM yyyy"
                                value={formik.values?.[section.key]?.[field.key]}
                                onChange={(val) => formik.setFieldValue(fieldPath, val)}
                                error={formik.errors?.[section.key]?.[field.key]}
                                touched={formik.touched?.[section.key]?.[field.key]}
                              />
                            </Box>
                          </Grid>
                        </Col>
                      </Row>
                    )
                  case "timeField":
                    return (
                      <Row className="w-100 m-0 mt-4" key={fieldIndex}>
                        <Col lg="12">
                          <AppTextField
                            id={fieldPath}
                            type="time"
                            fullWidth
                            label={capitalize(field.label)}
                            isUpperCase={true}
                            value={formik.values?.[section.key]?.[field.key]}
                            onChange={(e) => formik.setFieldValue(fieldPath, e.target.value)}
                            error={
                              formik.touched?.[section.key]?.[field.key] &&
                              Boolean(formik.errors?.[section.key]?.[field.key])
                            }
                            touched={formik.touched?.[section.key]?.[field.key]}
                            helperText={formik.errors?.[section.key]?.[field.key]}
                          />
                        </Col>
                      </Row>
                    )

                  case "imageUpload":
                    return (
                      <Row className="w-100 m-0 mt-4" key={fieldIndex}>
                        <Col lg="12">
                          <ImageUploadField
                            label={capitalize(field.label)}
                            isVariant={true}
                            placeholder={`Upload ${field.label}`}
                            value={formik.values?.[section.key]?.[field.key]}
                            onChange={(e) =>
                              onImageUpload(e, fieldPath, field.key, field.type, section.key)
                            }
                            onDelete={(index) =>
                              handleImageDelete(
                                formik,
                                fieldPath,
                                index,
                                field.type,
                                field.key,
                                section.key
                              )
                            }
                            error={
                              formik.touched?.[section.key]?.[field.key] &&
                              Boolean(formik.errors?.[section.key]?.[field.key])
                            }
                            touched={formik.touched?.[section.key]?.[field.key]}
                            helperText={formik.errors?.[section.key]?.[field.key]}
                          />
                        </Col>
                      </Row>
                    )
                  case "restaurantImageUpload":
                    return (
                      <Row className="w-100 m-0 mt-4" key={fieldIndex}>
                        <Col lg="12">
                          <ImageUploadPreview
                            label={capitalize(field.label)}
                            isVariant={true}
                            placeholder={`Enter ${field.label}`}
                            value={formik.values?.[section.key]?.[field.key]}
                            onChange={(e) => onImageUpload(e, fieldPath, field.key, field.type)}
                            onDelete={(index) =>
                              handleImageDelete(formik, fieldPath, index, field.type, field.key)
                            }
                            type="fields"
                            error={
                              formik.touched?.[section.key]?.[field.key] &&
                              Boolean(formik.errors?.[section.key]?.[field.key])
                            }
                            touched={formik.touched?.[section.key]?.[field.key]}
                            helperText={formik.errors?.[section.key]?.[field.key]}
                          />
                        </Col>
                      </Row>
                    )
                  case "uploadPdf":
                    return (
                      <Row className="w-100 m-0 mt-4" key={fieldIndex}>
                        <Col lg="12">
                          <UploadPdfField
                            title={capitalize(field.label)}
                            label={capitalize(field.label)}
                            formik={formik}
                            fieldPath={fieldPath}
                            type="fields"
                            placeholder={`Enter ${field.label}`}
                          />
                        </Col>
                      </Row>
                    )

                  default:
                    return null
                }
              })}
            </Box>
          ))}
        </Box>
        <Grid container alignItems="center" className="mb-3 mt-4">
          <Typography variant="h2" style={{ marginRight: "16px", whiteSpace: "nowrap" }}>
            Soundbite
          </Typography>
          <div
            style={{
              flexGrow: 1,
              height: "1px",
              backgroundColor: "#E0E0E0"
            }}
          />
        </Grid>
        <Row className="w-100 m-0 mt-4">
          <Col lg="12">
            <AppTextArea
              label="Soundbite"
              minRows={5}
              maxRows={15}
              placeholder={`Enter Soundbite`}
              value={formik.values?.soundbite}
              onChange={formik.handleChange("soundbite")}
              onBlur={formik.handleBlur("soundbite")}
              error={Boolean(formik.errors?.soundbite)}
              touched={formik.touched?.soundbite}
              helperText={formik.errors?.soundbite}
            />
          </Col>
          <Row
            className="w-100 m-0 mt-2 mb-2  align-items-center"
            style={{ justifyContent: "space-between" }}>
            <Col lg="4" className="p-0">
              <AppTextField
                id={"soundbite_voice_id"}
                type="text"
                fullWidth
                label="Soundbite Voice Id"
                isUpperCase={true}
                placeholder={`Enter Soundbite Voice Id`}
                value={formik.values?.soundbite_voice_id}
                onChange={formik.handleChange("soundbite_voice_id")}
                onBlur={formik.handleBlur("soundbite_voice_id")}
                error={Boolean(formik.errors?.soundbite_voice_id)}
                touched={formik.touched?.soundbite_voice_id}
                helperText={formik.errors?.soundbite_voice_id}
              />
            </Col>
            <Col lg="8" className="mt-3">
              <Row className="w-100 m-0 mt-2 mb-2 justify-content-end align-items-center">
                <Col lg="3" className="d-flex justify-content-end p-0">
                  {(soundbites?.url || formik.values?.soundbite_url) && !formik.touched?.soundbite && (
                    <AppButton styles={{ padding: "10px 16px" }} onClick={toggleSoundbite}>
                      {isPlaying ? (
                        <>
                          <Pause sx={{ height: 28, width: 28, marginRight: 1 }} />
                          Stop
                        </>
                      ) : (
                        <>
                          <PlayArrow sx={{ height: 28, width: 28, marginRight: 1 }} />
                          Play
                        </>
                      )}
                    </AppButton>
                  )}
                </Col>
                <Col lg="5" className="d-flex justify-content-end p-0">
                  <AppButton
                    sx={{ padding: "13px 16px" }}
                    disabled={!formik.values?.soundbite}
                    onClick={() => handleSoundbite(formik.values?.soundbite)}>
                    Generate Sound Bite
                  </AppButton>
                </Col>
              </Row>
            </Col>
          </Row>
        </Row>
        <Grid container alignItems="center" className="mt-4">
          <Typography variant="h2" style={{ marginRight: "16px", whiteSpace: "nowrap" }}>
            Did You know
          </Typography>
          <div
            style={{
              flexGrow: 1,
              height: "1px",
              backgroundColor: "#E0E0E0"
            }}
          />
        </Grid>
        <DidYouKnowFormHotel
          formik={formik}
          values={formik.values?.["did_you_know"]}
          name={"did_you_know"}
          buttonText={"Add Did you know"}
        />
        <Grid container alignItems="center" className="mt-4">
          <Typography variant="h2" style={{ marginRight: "16px", whiteSpace: "nowrap" }}>
            History
          </Typography>
          <div
            style={{
              flexGrow: 1,
              height: "1px",
              backgroundColor: "#E0E0E0"
            }}
          />
        </Grid>
        <HistoryFormHotel formik={formik} />

        <Box className="mt-5 d-flex justify-content-around">
          <AppButton
            onClick={formik.handleSubmit}
            sx={() => ({
              height: "48px",
              padding: "10px 50px"
            })}>
            {isEdit ? buttonText.UPDATE : buttonText.SAVE}
          </AppButton>
        </Box>
      </DialogContent>
    </StyledDialog>
  )
}

export default DynamicItemForm
